import { Role } from '@/Enums/Users/Role.enum';
import { SCREEN_LABELS } from '@/constants/routes';
import { USER_ROLE_LABELS } from '@/constants/userManagement';
import { DEFAULT_SCREENS_BY_ROLE } from '@/constants/userManagement/userForm';
import { isRoleAllowedWhitelabelSelect } from '../roles';

export const formatUserResponseData = (userData, orgs = [], whitelabels = [], clients = [], meRole, meWhitelabelId) => {
  if (!userData) return {};
  const {
    id,
    first_name,
    last_name,
    email,
    phone_number,
    role_id,
    clients: userClients,
    agency,
    advertiser,
    screens,
    whitelabel_id: userWhitelabel,
    can_approve_campaigns: canApproveCampaigns,
    can_view_dsp_platform: canViewDspPlatform,
    can_view_wholesale_spend_cpm: canViewWholesaleSpendCpm,
    can_view_client_spend_cpm: canViewClientSpendCpm,
    can_view_raw_inventory: canViewRawInventory,
    can_view_attribution: canViewAttribution,
  } = userData;

  const organizationLevelPresent = isRoleAllowedWhitelabelSelect(meRole);
  const meWhitelabel = { id: meWhitelabelId };

  const userObject = {
    id,
    firstName: first_name,
    lastName: last_name,
    email,
    phone: phone_number,
    userRole: role_id,
    accessManagement: Object.fromEntries(screens.map(screen => [screen.name, screen.access])),
    canApproveCampaigns,
    canViewDspPlatform,
    canViewWholesaleSpendCpm,
    canViewClientSpendCpm,
    canViewRawInventory,
    canViewAttribution,
  };

  switch (role_id) {
    case Role.ORG_ADMIN: {
      userObject.organization = orgs?.find(org => org.id === userWhitelabel) ?? {};
      return userObject;
    }
    case Role.WHITELABEL_ADMIN: {
      if (organizationLevelPresent) {
        const whitelabel = whitelabels?.find(wl => wl.id === userWhitelabel) ?? {};
        userObject.organization = orgs.find(o => o.id === whitelabel.parentId) ?? {};
        userObject.whitelabel = whitelabel;
      } else {
        userObject.whitelabel = meWhitelabel;
      }
      return userObject;
    }
    case Role.CLIENT: {
      userObject.clients = userClients.map(client => ({
        value: client.id,
        label: client.name,
        whitelabel: client.whitelabel_id,
      }));
      if (organizationLevelPresent) {
        userObject.whitelabel = [...whitelabels, ...orgs].find(wl => wl.id === userWhitelabel);
      } else {
        userObject.whitelabel = meWhitelabel;
      }
      return userObject;
    }
    case Role.AGENCY: {
      userObject.client = clients?.find(client => client.value === agency?.client_id) || {};
      userObject.agency = {
        value: agency?.id,
        label: agency?.name,
      };
      return userObject;
    }
    case Role.ADVERTISER: {
      userObject.client = clients?.find(client => client.value === advertiser?.client_id) || {};
      userObject.advertiser = {
        value: advertiser?.id,
        label: advertiser?.name,
      };
      return userObject;
    }
    default:
      return userObject;
  }
};

export const formatUsersResponseData = ({ data } = {}, clients) => {
  if (!data?.data) {
    return { usersTotal: 0, users: [] };
  }
  const usersTotal = data.meta?.total;
  const users = data.data?.map(user => {
    const userObject = {
      id: user.id,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email,
      role: {
        label: USER_ROLE_LABELS[user.role_id],
        value: user.role_id,
      },
    };
    switch (user.role_id) {
      case Role.CLIENT: {
        userObject.clients = user.clients?.map(client => ({ value: client.id, label: client.name })) || [];
        return userObject;
      }
      case Role.AGENCY: {
        userObject.clients = [clients?.find(client => client.value === user.agency.client_id)];
        userObject.agency = user.agency;
        return userObject;
      }
      case Role.ADVERTISER: {
        userObject.clients = null;
        userObject.advertiser = user.advertiser;
        return userObject;
      }
      default:
        return userObject;
    }
  });
  return { users, usersTotal };
};

export const formatClientsResponseData = clients => {
  if (!clients?.length) return [];
  return clients
    .map(client => {
      const { id, name, agencies, advertisers, whitelabel_id: whitelabel } = client;

      return {
        value: id,
        label: name,
        advertisers: advertisers?.length
          ? advertisers.map(({ id: _id, name: _name }) => ({ value: _id, label: _name }))
          : [],
        agencies: agencies?.length ? agencies.map(({ id: _id, name: _name }) => ({ value: _id, label: _name })) : [],
        whitelabel,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const formatScreensReferenceResponseData = ({ data }) => {
  if (!data) return DEFAULT_SCREENS_BY_ROLE;
  return Object.fromEntries(
    Object.entries(data).map(([roleId, screens]) => {
      return [
        +roleId,
        screens.map(screen => ({
          value: screen,
          label: SCREEN_LABELS[screen],
        })),
      ];
    }),
  );
};
